import React, { useState, useEffect } from "react";
import { Modal } from "react-bootstrap";
import { Link } from "react-router-dom";
import { connect } from "react-redux";
import moment from "moment";
import { LazyLoadImage } from "react-lazy-load-image-component";
import api from "../../../../api/api";
import { fetchIndices } from "../../Indices/actions";
import HistoryDateFilter from "./HistoryDateFilter";
import { getPathList } from "./historyUtils";

const UserFileHistoryModal = ({ show, onClose, user, t, indices, fetchIndices }) => {
    const [historyList, setHistoryList] = useState([]);
    const [filesDetails, setFilesDetails] = useState({});
    const [loading, setLoading] = useState(true);
    const [startDate, setStartDate] = useState(moment().subtract(6, 'days'));
    const [endDate, setEndDate] = useState(moment());
    const [searchText, setSearchText] = useState("");
    const [searchMode, setSearchMode] = useState("date");
    const [count, setCount] = useState(20);

    const fetchData = (currentMode = searchMode, start = startDate, end = endDate, cnt = count) => {
        if (!user) return;
        setLoading(true);

        if (!indices || !indices.nodes || indices.nodes.length === 0) {
            fetchIndices();
        }

        let url = "";
        if (currentMode === "date") {
            url = `/UserFileHistory?userId=${user.Id}&startDate=${start.format('DD/MM/YYYY')}&endDate=${end.format('DD/MM/YYYY')}&count=1000&searchMode=date`;
        } else {
            url = `/UserFileHistory?userId=${user.Id}&count=${cnt}&searchMode=count`;
        }

        api.get(url)
            .then(resp => {
                const historyData = resp.data?.data || [];
                setHistoryList(historyData);

                if (historyData.length === 0) {
                    setLoading(false);
                    return;
                }

                const uniqueFileIds = [...new Set(historyData.map(item => item.FileId))];
                Promise.all(
                    uniqueFileIds.map(id =>
                        api.get(`/EducationalFiles/${id}`)
                            .then(r => r.data?.data)
                            .catch(() => null)
                    )
                ).then(detailsList => {
                    const detailsMap = {};
                    detailsList.forEach(detail => {
                        if (detail) {
                            detailsMap[detail.Id] = detail;
                        }
                    });
                    setFilesDetails(detailsMap);
                    setLoading(false);
                }).catch(() => {
                    setLoading(false);
                });
            })
            .catch(() => {
                setLoading(false);
            });
    };

    useEffect(() => {
        if (show && user) {
            fetchData();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [show, user, indices, fetchIndices]);

    // Client-side filtering by selected date range and search text
    const filteredHistoryList = historyList.filter(item => {
        const fileDetail = filesDetails[item.FileId];
        if (!fileDetail) return false;

        // Date filter
        if (searchMode === "date") {
            if (item.CreationDate) {
                const itemDate = moment(item.CreationDate, "YYYY-MM-DD HH:mm");
                const inDateRange = itemDate.isSameOrAfter(startDate, 'day') && itemDate.isSameOrBefore(endDate, 'day');
                if (!inDateRange) return false;
            } else {
                return false;
            }
        }

        // Search text filter
        if (searchText) {
            const query = searchText.toLowerCase();
            const fileName = fileDetail.Name ? fileDetail.Name.toLowerCase() : "";
            if (!fileName.includes(query)) return false;
        }

        return true;
    });

    return (
        <Modal size="lg" centered show={show} onHide={onClose} className="admin-history-modal" enforceFocus={false}>
            <Modal.Header closeButton className="border-0 pb-0">
                <Modal.Title className="w-100">
                    <div className="d-flex align-items-center">
                        <div className="history-modal-icon-wrapper ml-3 text-success">
                            <i className="fad fa-download"></i>
                        </div>
                        <div className="text-right">
                            <div className="h5 mb-1 font-weight-bold text-dark">
                                {t("Pages.UsersControl.action.downloadFiles") || "الملفات المحملة"}
                            </div>
                            <div className="small text-muted">{user?.Name}</div>
                        </div>
                    </div>
                </Modal.Title>
            </Modal.Header>
            <Modal.Body className="pt-4 pb-4">
                {/* Date Filter */}
                <HistoryDateFilter
                    startDate={startDate}
                    endDate={endDate}
                    searchMode={searchMode}
                    count={count}
                    searchText={searchText}
                    showSearchModeSelect={true}
                    onApply={(mode, start, end, cnt, search) => {
                        setSearchMode(mode);
                        setStartDate(start);
                        setEndDate(end);
                        setCount(cnt);
                        setSearchText(search);
                        fetchData(mode, start, end, cnt);
                    }}
                    t={t}
                />

                {loading ? (
                    <div className="text-center py-5">
                        <i className="fad fa-sync fa-spin fa-2x text-muted mb-2"></i>
                        <div className="text-muted small">{t("Pages.UsersControl.Loading") || "جاري التحميل..."}</div>
                    </div>
                ) : filteredHistoryList.length === 0 ? (
                    <div className="text-center py-5 text-muted">
                        <i className="fal fa-file-search fa-2x mb-2 d-block"></i>
                        <span>{t("Pages.UsersControl.NotFound") || "لا توجد نتائج"}</span>
                    </div>
                ) : (
                    <div className="history-modal-list scrollbar-custom mx-0" style={{ direction: 'rtl' }}>
                        {filteredHistoryList.map((item) => {
                            const fileDetail = filesDetails[item.FileId];
                            if (!fileDetail) return null;

                            const pathList = getPathList(indices?.nodes, fileDetail.Index?.Id, 0);

                            return (
                                <div key={item.Id} className="w-100 mb-3 compact-history-card">
                                    <div className="compact-history-row">
                                        <div className="compact-history-left">
                                            <div className="compact-history-icon-box p-0 overflow-hidden" style={{ background: '#f8fafc' }}>
                                                <LazyLoadImage
                                                    src={fileDetail.Icon || "/assets/images/logo/130p150.png"}
                                                    alt={fileDetail.Name}
                                                    style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                                                />
                                            </div>
                                            <div className="compact-history-info">
                                                <h6 className="compact-history-title">{fileDetail.Name}</h6>
                                                {pathList && pathList.length > 0 && (
                                                    <div className="compact-history-path">
                                                        {pathList.map((node, index) => (
                                                            <span key={node.id}>
                                                                <Link to={`/Tracks/Files/${node.id}`} className="text-muted hover-underline" onClick={onClose}>
                                                                    {node.label}
                                                                </Link>
                                                                {index < pathList.length - 1 && <i className="fal fa-chevron-left mx-2" style={{ fontSize: '0.65rem' }}></i>}
                                                            </span>
                                                        ))}
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                        <div className="compact-history-right">
                                            <span className="compact-history-badge blue">
                                                <i className="fal fa-calendar-alt ml-1"></i>
                                                <span>{item.CreationDate}</span>
                                            </span>
                                            {fileDetail.IsPublic && (
                                                <span className="compact-history-badge green">
                                                    <span>{t("Pages.EducationalFiles.Public") || "عام"}</span>
                                                </span>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                )}
            </Modal.Body>
            <Modal.Footer className="border-0 pt-0 text-center justify-content-center">
                <button type="button" className="btn btn-light px-5 py-2" style={{ borderRadius: '10px', fontWeight: '600' }} onClick={onClose}>
                    {t("Cancel") || "إلغاء"}
                </button>
            </Modal.Footer>
        </Modal>
    );
};

const mapStateToProps = (state) => ({
    indices: state.indices
});

export default connect(mapStateToProps, { fetchIndices })(UserFileHistoryModal);
